import { createFeatureSelector, createSelector } from '@ngrx/store';

import { compact, includes, inRange, isNull, map } from 'lodash-es';
import { DateTime } from 'luxon';

import {
  amountPrefix,
  cleanUpNullishMaskValue,
  rateAmountMask,
  rateAmountSymbolRegExPattern,
  rateMask,
  ratePostfix
} from '../../constants/maskito.constants';
import { NetSheetPartyTypeEnum, NetSheetSplitOptionsEnum, NetSheetStateEnum } from '../../enums/net-sheet';
import { NetSheetInputData, NetSheetSalesCommissionBrokerCommissionTotalSettings } from '../../typings/net-sheet';
import { netSheetFeatureKey, State } from '../reducers/net-sheet.reducer';

export const selectNetSheetFeature = createFeatureSelector<State>(netSheetFeatureKey);

export const selectCustomerInformationPartyTypes = createSelector(
  selectNetSheetFeature,
  (state) => state.customerInformationPartyTypes
);

export const selectCustomerInformationPartyType = createSelector(
  selectNetSheetFeature,
  (state) => state.customerInformationPartyType
);

export const selectCustomerInformationFieldsSettings = createSelector(selectNetSheetFeature, (state) => ({
  customerInformationPartyFirstName: {
    required: state.customerInformationPartyFirstNameRequired,
    visible: state.customerInformationPartyFirstNameVisible
  },
  customerInformationPartyFullName: {
    required: state.customerInformationPartyFullNameRequired,
    visible: state.customerInformationPartyFullNameVisible
  },
  customerInformationPartyLastName: {
    required: state.customerInformationPartyLastNameRequired,
    visible: state.customerInformationPartyLastNameVisible
  }
}));

export const selectCustomerInformationFieldsData = createSelector(selectNetSheetFeature, (state) => ({
  customerInformationPartyFirstName: state.customerInformationPartyFirstName,
  customerInformationPartyFullName: state.customerInformationPartyFullName,
  customerInformationPartyLastName: state.customerInformationPartyLastName,
  customerInformationPartyType: state.customerInformationPartyType
}));

export const selectTransactionInformationLocationPropertyStates = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationPropertyStates
);

export const selectTransactionInformationLocationEscrowStates = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationEscrowStates
);

export const selectTransactionInformationLocationPropertyCounties = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationPropertyCounties
);

export const selectTransactionInformationLocationEscrowCounties = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationEscrowCounties
);

export const selectTransactionInformationLocationPropertyCities = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationPropertyCities
);

export const selectTransactionInformationLocationPropertyZips = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationPropertyZips
);

export const selectTransactionInformationLocationEscrowCities = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationEscrowCities
);

export const selectTransactionInformationLocationEscrowBranches = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationEscrowBranches
);

export const selectTransactionInformationLocationPropertyState = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationPropertyState
);

export const selectTransactionInformationLocationPropertyCounty = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationPropertyCounty
);

export const selectTransactionInformationLocationEscrowState = createSelector(
  selectNetSheetFeature,
  (state) => state.transactionInformationEscrowState
);

export const selectTransactionInformationLocationFieldsData = createSelector(selectNetSheetFeature, (state) => ({
  transactionInformationBrokerageFee: state.transactionInformationBrokerageFee,
  transactionInformationBuyerCreditAmount: state.transactionInformationBuyerCreditAmount,
  transactionInformationCityTransferTaxSplit: state.transactionInformationCityTransferTaxSplit,
  transactionInformationClosingDate: state.transactionInformationClosingDate,
  transactionInformationCountyTransferTaxSplit: state.transactionInformationCountyTransferTaxSplit,
  transactionInformationEscrowBranch: state.transactionInformationEscrowBranch,
  transactionInformationEscrowCity: state.transactionInformationEscrowCity,
  transactionInformationEscrowCounty: state.transactionInformationEscrowCounty,
  transactionInformationEscrowFeeSplit: state.transactionInformationEscrowFeeSplit,
  transactionInformationEscrowState: state.transactionInformationEscrowState,
  transactionInformationFirpta: state.transactionInformationFirpta,
  transactionInformationFtb: state.transactionInformationFtb,
  transactionInformationHarpta: state.transactionInformationHarpta,
  transactionInformationHazardInsuranceAmount: state.transactionInformationHazardInsuranceAmount,
  transactionInformationLoanPrimaryAmount: state.transactionInformationLoanPrimaryAmount,
  transactionInformationLoanPrimaryInterestRate: state.transactionInformationLoanPrimaryInterestRate,
  transactionInformationLoanPrimaryOriginationFees: state.transactionInformationLoanPrimaryOriginationFees,
  transactionInformationMortgagePayoffCount: state.transactionInformationMortgagePayoffCount,
  transactionInformationNaturalHazardDisclosure: state.transactionInformationNaturalHazardDisclosure,
  transactionInformationNewLoan: state.transactionInformationNewLoan,
  transactionInformationNotaryFee: state.transactionInformationNotaryFee,
  transactionInformationOwnersExemption: state.transactionInformationOwnersExemption,
  transactionInformationOwnersPolicySplit: state.transactionInformationOwnersPolicySplit,
  transactionInformationPersonalResidence: state.transactionInformationPersonalResidence,
  transactionInformationPropertyAddress: state.transactionInformationPropertyAddress,
  transactionInformationPropertyCity: state.transactionInformationPropertyCity,
  transactionInformationPropertyCounty: state.transactionInformationPropertyCounty,
  transactionInformationPropertyIncorporatedArea: state.transactionInformationPropertyIncorporatedArea,
  transactionInformationPropertyState: state.transactionInformationPropertyState,
  transactionInformationPropertyZip: state.transactionInformationPropertyZip,
  transactionInformationReo: state.transactionInformationReo,
  transactionInformationSalesPrice: state.transactionInformationSalesPrice,
  transactionInformationSewerBill: state.transactionInformationSewerBill,
  transactionInformationShortSale: state.transactionInformationShortSale,
  transactionInformationSubEscrow: state.transactionInformationSubEscrow,
  transactionInformationSurveyAmendmentSplit: state.transactionInformationSurveyAmendmentSplit,
  transactionInformationWarrantyPremiumAmount: state.transactionInformationWarrantyPremiumAmount,
  transactionInformationWaterBill: state.transactionInformationWaterBill
}));

export const selectTransactionInformationLocationFieldsSettings = createSelector(selectNetSheetFeature, (state) => ({
  transactionInformationBrokerageFee: {
    visible: state.transactionInformationBrokerageFeeVisible
  },
  transactionInformationBuyerCreditAmount: {
    visible: state.transactionInformationBuyerCreditAmountVisible
  },
  transactionInformationCityTransferTaxSplit: {
    visible: state.transactionInformationCityTransferTaxSplitVisible
  },
  transactionInformationClosingDate: {
    required: state.transactionInformationClosingDateRequired,
    visible: state.transactionInformationClosingDateVisible
  },
  transactionInformationCountyTransferTaxSplit: {
    visible: state.transactionInformationCountyTransferTaxSplitVisible
  },
  transactionInformationEscrowBranch: {
    required: state.transactionInformationEscrowBranchRequired,
    visible: state.transactionInformationEscrowBranchVisible
  },
  transactionInformationEscrowCity: {
    visible: state.transactionInformationEscrowCityVisible
  },
  transactionInformationEscrowCounty: {
    required: state.transactionInformationEscrowCountyRequired,
    visible: state.transactionInformationEscrowCountyVisible
  },
  transactionInformationEscrowFeeSplit: {
    visible: state.transactionInformationEscrowFeeSplitVisible
  },
  transactionInformationEscrowState: {
    required: state.transactionInformationEscrowStateRequired,
    visible: state.transactionInformationEscrowStateVisible
  },
  transactionInformationFirpta: {
    visible: state.transactionInformationFirptaVisible
  },
  transactionInformationFtb: {
    visible: state.transactionInformationFtbVisible
  },
  transactionInformationHarpta: {
    visible: state.transactionInformationHarptaVisible
  },
  transactionInformationHazardInsuranceAmount: {
    visible: state.transactionInformationHazardInsuranceAmountVisible
  },
  transactionInformationLoanPrimaryAmount: {
    required: state.transactionInformationLoanPrimaryAmountRequired,
    visible: state.transactionInformationLoanPrimaryAmountVisible
  },
  transactionInformationLoanPrimaryInterestRate: {
    visible: state.transactionInformationLoanPrimaryInterestRateVisible
  },
  transactionInformationLoanPrimaryOriginationFees: {
    visible: state.transactionInformationLoanPrimaryOriginationFeesVisible
  },
  transactionInformationMortgagePayoffCount: {
    visible: state.transactionInformationMortgagePayoffCountVisible
  },
  transactionInformationNaturalHazardDisclosure: {
    required: state.transactionInformationNaturalHazardDisclosureRequired,
    visible: state.transactionInformationNaturalHazardDisclosureVisible
  },
  transactionInformationNewLoan: {
    visible: state.transactionInformationNewLoanVisible
  },
  transactionInformationNotaryFee: {
    visible: state.transactionInformationNotaryFeeVisible
  },
  transactionInformationOwnersExemption: {
    visible: state.transactionInformationOwnersExemptionVisible
  },
  transactionInformationOwnersPolicySplit: {
    visible: state.transactionInformationOwnersPolicySplitVisible
  },
  transactionInformationPersonalResidence: {
    visible: state.transactionInformationPersonalResidenceVisible
  },
  transactionInformationPropertyAddress: {
    required: state.transactionInformationPropertyAddressRequired,
    visible: state.transactionInformationPropertyAddressVisible
  },
  transactionInformationPropertyCity: {
    required: state.transactionInformationPropertyCityRequired,
    visible: state.transactionInformationPropertyCityVisible
  },
  transactionInformationPropertyCounty: {
    required: state.transactionInformationPropertyCountyRequired,
    visible: true
  },
  transactionInformationPropertyIncorporatedArea: {
    visible: state.transactionInformationPropertyIncorporatedAreaVisible
  },
  transactionInformationPropertyState: {
    required: state.transactionInformationPropertyStateRequired,
    visible: true
  },
  transactionInformationPropertyZip: {
    visible: state.transactionInformationPropertyZipVisible
  },
  transactionInformationReo: {
    visible: state.transactionInformationReoVisible
  },
  transactionInformationSalesPrice: {
    required: state.transactionInformationSalesPriceRequired,
    visible: state.transactionInformationSalesPriceVisible
  },
  transactionInformationSewerBill: {
    visible: state.transactionInformationSewerBillVisible
  },
  transactionInformationShortSale: {
    visible: state.transactionInformationShortSaleVisible
  },
  transactionInformationSubEscrow: {
    visible: state.transactionInformationSubEscrowVisible
  },
  transactionInformationSurveyAmendmentSplit: {
    visible: state.transactionInformationSurveyAmendmentSplitVisible
  },
  transactionInformationWarrantyPremiumAmount: {
    visible: state.transactionInformationWarrantyPremiumAmountVisible
  },
  transactionInformationWaterBill: {
    visible: state.transactionInformationWaterBillVisible
  }
}));

export const selectTransactionInformationMiscBuyerCreditAmountLabel = createSelector(selectNetSheetFeature, (state) => {
  let result = '';

  switch (state.customerInformationPartyType) {
    case NetSheetPartyTypeEnum.Borrower:
      result = 'Credit to Buyer';
      break;
    case NetSheetPartyTypeEnum.Buyer:
      result = 'Credit from Seller';
      break;
    case NetSheetPartyTypeEnum.Seller:
      result =
        state.transactionInformationPropertyState === NetSheetStateEnum.TX
          ? 'Seller Paid Closing Costs for Buyer'
          : 'Credit to Buyer';
      break;
  }

  return result;
});

export const selectMortgageFieldsData = createSelector(selectNetSheetFeature, (state) => ({
  mortgagePrimaryBalance: state.mortgagePrimaryBalance,
  mortgagePrimaryInterestRate: state.mortgagePrimaryInterestRate,
  mortgagePrimaryLienCloseDate: state.mortgagePrimaryLienCloseDate,
  mortgagePrimaryPrepayPenalty: state.mortgagePrimaryPrepayPenalty,
  mortgageSecondaryBalance: state.mortgageSecondaryBalance,
  mortgageSecondaryInterestRate: state.mortgageSecondaryInterestRate,
  mortgageSecondaryPrepayPenalty: state.mortgageSecondaryPrepayPenalty
}));

export const selectMortgageFieldsSettings = createSelector(selectNetSheetFeature, (state) => ({
  primary: {
    mortgagePrimaryBalance: {
      visible: state.mortgagePrimaryBalanceVisible
    },
    mortgagePrimaryInterestRate: {
      visible: state.mortgagePrimaryInterestRateVisible
    },
    mortgagePrimaryLienCloseDate: {
      visible: state.mortgagePrimaryLienCloseDateVisible
    },
    mortgagePrimaryPrepayPenalty: {
      visible: state.mortgagePrimaryPrepayPenaltyVisible
    }
  },
  secondary: {
    mortgageSecondaryBalance: {
      visible: state.mortgageSecondaryBalanceVisible
    },
    mortgageSecondaryInterestRate: {
      visible: state.mortgageSecondaryInterestRateVisible
    },
    mortgageSecondaryPrepayPenalty: {
      visible: state.mortgageSecondaryPrepayPenaltyVisible
    }
  }
}));

export const selectSalesCommissionFieldsData = createSelector(selectNetSheetFeature, (state) => ({
  salesCommissionBrokerCommissionListing: state.salesCommissionBrokerCommissionListing,
  salesCommissionBrokerCommissionSelling: state.salesCommissionBrokerCommissionSelling,
  salesCommissionBrokerCommissionTierRate: state.salesCommissionBrokerCommissionTierRate,
  salesCommissionBrokerCommissionTotal: state.salesCommissionBrokerCommissionTotal
}));
export const selectSalesCommissionFieldsSettings = createSelector(selectNetSheetFeature, (state) => ({
  salesCommissionBrokerCommissionListing: {
    required: state.salesCommissionBrokerCommissionListingRequired,
    visible: state.salesCommissionBrokerCommissionListingVisible
  },
  salesCommissionBrokerCommissionSelling: {
    required: state.salesCommissionBrokerCommissionSellingRequired,
    visible: state.salesCommissionBrokerCommissionSellingVisible
  },
  salesCommissionBrokerCommissionSplits: {
    visible: state.salesCommissionBrokerCommissionSplitsVisible
  },
  salesCommissionBrokerCommissionTaxListing: {
    visible: state.salesCommissionBrokerCommissionTaxListingVisible
  },
  salesCommissionBrokerCommissionTaxSelling: {
    visible: state.salesCommissionBrokerCommissionTaxSellingVisible
  },
  salesCommissionBrokerCommissionTaxTotal: {
    visible: state.salesCommissionBrokerCommissionTaxTotalVisible
  },
  salesCommissionBrokerCommissionTierRate: {
    visible: state.salesCommissionBrokerCommissionTierRateVisible
  },
  salesCommissionBrokerCommissionTotal: {
    required: state.salesCommissionBrokerCommissionTotalRequired,
    visible: state.salesCommissionBrokerCommissionTotalVisible
  }
}));
export const selectSalesCommissionsBrokerCommissionTotalSettings = createSelector(
  selectNetSheetFeature,
  (state): NetSheetSalesCommissionBrokerCommissionTotalSettings =>
    state.transactionInformationPropertyState === NetSheetStateEnum.OH
      ? {
          inputMode: 'decimal',
          label: 'Balance of Commission',
          mask: rateMask,
          placeholder: '%'
        }
      : {
          inputMode: 'text',
          label: 'Broker / Realtor Compensation',
          mask: rateAmountMask(20),
          placeholder: '$ / %'
        }
);

export const selectTaxesFieldsData = createSelector(selectNetSheetFeature, (state) => ({
  taxes: state.taxes,
  taxesHomeownersDuesAmount: state.taxesHomeownersDuesAmount,
  taxesHomeownersDuesDueDate: state.taxesHomeownersDuesDueDate,
  taxesHomeownersDuesTerm: state.taxesHomeownersDuesTerm,
  taxesNextInstallment: state.taxesNextInstallment,
  taxesPaidToDateDropdown: state.taxesPaidToDateDropdown,
  taxesPaidToDatePicker: state.taxesPaidToDatePicker,
  taxesTerm: state.taxesTerm
}));
export const selectTaxesFieldsSettings = createSelector(selectNetSheetFeature, (state) => ({
  taxes: {
    visible: state.taxesVisible
  },
  taxesHomeownersDuesAmount: {
    visible: state.taxesHomeownersDuesAmountVisible
  },
  taxesHomeownersDuesDueDate: {
    visible: state.taxesHomeownersDuesDueDateVisible
  },
  taxesHomeownersDuesPaid: {
    visible: state.taxesHomeownersDuesPaidVisible
  },
  taxesHomeownersDuesTerm: {
    visible: state.taxesHomeownersDuesTermVisible
  },
  taxesNextInstallment: {
    visible: state.taxesNextInstallmentVisible
  },
  taxesPaid: {
    visible: state.taxesPaidVisible
  },
  taxesPaidToDateDropdown: {
    visible: state.taxesPaidToDateDropdownVisible
  },
  taxesPaidToDatePicker: {
    visible: state.taxesPaidToDatePickerVisible
  },
  taxesTerm: {
    visible: state.taxesTermVisible
  }
}));
export const selectTaxesPaidToDateDropdownData = createSelector(selectNetSheetFeature, (state) => {
  let data: string[] = [];

  if (
    !isNull(state.transactionInformationPropertyState) &&
    [NetSheetStateEnum.CA, NetSheetStateEnum.OH].includes(state.transactionInformationPropertyState)
  ) {
    const currentDate = DateTime.now();

    if (currentDate.month < 7) {
      data = [
        currentDate.set({ day: 1, month: 1 }).toFormat('MM/dd/yyyy'),
        currentDate.set({ day: 1, month: 7 }).toFormat('MM/dd/yyyy')
      ];
    } else {
      data = [
        currentDate.set({ day: 1, month: 7 }).toFormat('MM/dd/yyyy'),
        currentDate.plus({ years: 1 }).set({ day: 1, month: 1 }).toFormat('MM/dd/yyyy')
      ];
    }
  }

  return data;
});
export const selectMisItemsFields = createSelector(selectNetSheetFeature, (state) => ({
  data: {
    miscItems: state.miscItems
  }
}));
export const selectMiscItemsFieldsData = createSelector(selectMisItemsFields, ({ data }) => data);

export const selectUserInformationFieldsData = createSelector(selectNetSheetFeature, (state) => ({
  userInformationCompany: state.userInformationCompany,
  userInformationEmail: state.userInformationEmail,
  userInformationFirstName: state.userInformationFirstName,
  userInformationLastName: state.userInformationLastName,
  userInformationPhone: state.userInformationPhone
}));

export const selectNetSheetCalculatorRequestData = createSelector(
  selectNetSheetFeature,

  (state): NetSheetInputData => {
    const salesPrice = cleanUpNullishMaskValue(state.transactionInformationSalesPrice, amountPrefix);
    const brokerageFee = cleanUpNullishMaskValue(state.transactionInformationBrokerageFee, amountPrefix);
    const sewerBill = cleanUpNullishMaskValue(state.transactionInformationSewerBill, amountPrefix);
    const waterBill = cleanUpNullishMaskValue(state.transactionInformationWaterBill, amountPrefix);
    const buyerCredit = cleanUpNullishMaskValue(state.transactionInformationBuyerCreditAmount, amountPrefix);
    const hazardInsurance = cleanUpNullishMaskValue(state.transactionInformationHazardInsuranceAmount, amountPrefix);
    const loanPrimaryBalance = cleanUpNullishMaskValue(state.transactionInformationLoanPrimaryAmount, amountPrefix);
    const loanPrimaryInterestRate = cleanUpNullishMaskValue(
      state.transactionInformationLoanPrimaryInterestRate,
      ratePostfix
    );
    const loanPrimaryOriginationFees = cleanUpNullishMaskValue(
      state.transactionInformationLoanPrimaryOriginationFees,
      rateAmountSymbolRegExPattern
    );
    const warrantyPremium = cleanUpNullishMaskValue(state.transactionInformationWarrantyPremiumAmount, amountPrefix);
    const mortgagePrimaryBalance = cleanUpNullishMaskValue(state.mortgagePrimaryBalance, amountPrefix);
    const mortgagePrimaryInterestRate = cleanUpNullishMaskValue(state.mortgagePrimaryInterestRate, ratePostfix);
    const mortgagePrimaryPrepayPenalty = cleanUpNullishMaskValue(state.mortgagePrimaryPrepayPenalty, amountPrefix);
    const mortgageSecondaryBalance = cleanUpNullishMaskValue(state.mortgageSecondaryBalance, amountPrefix);
    const mortgageSecondaryInterestRate = cleanUpNullishMaskValue(state.mortgageSecondaryInterestRate, ratePostfix);
    const mortgageSecondaryPrepayPenalty = cleanUpNullishMaskValue(state.mortgageSecondaryPrepayPenalty, amountPrefix);
    const hoaAmount = cleanUpNullishMaskValue(state.taxesHomeownersDuesAmount, amountPrefix);
    const taxesNextInstallment = cleanUpNullishMaskValue(state.taxesNextInstallment, amountPrefix);
    const naturalHazardDisclosure = cleanUpNullishMaskValue(
      state.transactionInformationNaturalHazardDisclosure,
      amountPrefix
    );
    const notaryFee = cleanUpNullishMaskValue(state.transactionInformationNotaryFee, amountPrefix);
    const taxes = cleanUpNullishMaskValue(state.taxes, amountPrefix);
    let taxesPaidToDate =
      state.taxesPaidToDatePickerVisible && !isNull(state.taxesPaidToDatePicker)
        ? DateTime.fromFormat(state.taxesPaidToDatePicker, 'MM/dd/yyyy').toJSDate()
        : null;
    if (state.taxesPaidToDateDropdownVisible && !isNull(state.taxesPaidToDateDropdown)) {
      taxesPaidToDate = DateTime.fromFormat(state.taxesPaidToDateDropdown, 'MM/dd/yyyy').toJSDate();
    }
    let brokerCommissionTotalRate: number | null = null;
    let brokerCommissionTotalAmount: number | null = null;
    if (state.transactionInformationPropertyState === NetSheetStateEnum.OH) {
      brokerCommissionTotalRate = cleanUpNullishMaskValue(state.salesCommissionBrokerCommissionTotal, ratePostfix);
    } else {
      const brokerCommissionTotal = cleanUpNullishMaskValue(
        state.salesCommissionBrokerCommissionTotal,
        rateAmountSymbolRegExPattern
      );
      if (!isNull(brokerCommissionTotal)) {
        if (inRange(brokerCommissionTotal, 0, 21)) {
          brokerCommissionTotalRate = brokerCommissionTotal;
        } else {
          brokerCommissionTotalAmount = brokerCommissionTotal;
        }
      }
    }
    const brokerCommissionTierRate = cleanUpNullishMaskValue(
      state.salesCommissionBrokerCommissionTierRate,
      ratePostfix
    );

    let brokerCommissionListingRate: number | null = null;
    let brokerCommissionListingAmount: number | null = null;

    if (includes(state.salesCommissionBrokerCommissionListing, '%')) {
      brokerCommissionListingRate = cleanUpNullishMaskValue(state.salesCommissionBrokerCommissionListing, ratePostfix);
    } else {
      brokerCommissionListingAmount = cleanUpNullishMaskValue(
        state.salesCommissionBrokerCommissionListing,
        amountPrefix
      );
    }

    let brokerCommissionSellingRate: number | null = null;
    let brokerCommissionSellingAmount: number | null = null;

    if (includes(state.salesCommissionBrokerCommissionSelling, '%')) {
      brokerCommissionSellingRate = cleanUpNullishMaskValue(state.salesCommissionBrokerCommissionSelling, ratePostfix);
    } else {
      brokerCommissionSellingAmount = cleanUpNullishMaskValue(
        state.salesCommissionBrokerCommissionSelling,
        amountPrefix
      );
    }

    return {
      ...(state.transactionInformationCityTransferTaxSplitVisible && {
        cityTransferTax: {
          ...(state.transactionInformationCityTransferTaxSplit == NetSheetSplitOptionsEnum.Seller && {
            buyerRate: 0,
            sellerRate: 100
          }),
          ...(state.transactionInformationCityTransferTaxSplit == NetSheetSplitOptionsEnum.Buyer && {
            buyerRate: 100,
            sellerRate: 0
          }),
          ...(state.transactionInformationCityTransferTaxSplit == NetSheetSplitOptionsEnum.Split && {
            buyerRate: 50,
            sellerRate: 50
          })
        }
      }),
      ...(state.transactionInformationCountyTransferTaxSplitVisible && {
        countyTransferTax: {
          ...(state.transactionInformationCountyTransferTaxSplit == NetSheetSplitOptionsEnum.Seller && {
            buyerRate: 0,
            sellerRate: 100
          }),
          ...(state.transactionInformationCountyTransferTaxSplit == NetSheetSplitOptionsEnum.Buyer && {
            buyerRate: 100,
            sellerRate: 0
          }),
          ...(state.transactionInformationCountyTransferTaxSplit == NetSheetSplitOptionsEnum.Split && {
            buyerRate: 50,
            sellerRate: 50
          })
        }
      }),
      ...(state.transactionInformationBrokerageFeeVisible &&
        !isNull(brokerageFee) && {
          brokerageFee: {
            amount: brokerageFee
          }
        }),
      ...(state.transactionInformationSewerBillVisible &&
        !isNull(sewerBill) && {
          sewerBill: {
            amount: sewerBill
          }
        }),
      ...(state.transactionInformationWaterBillVisible &&
        !isNull(waterBill) && {
          waterBill: {
            amount: waterBill
          }
        }),
      ...(state.transactionInformationBuyerCreditAmountVisible &&
        !isNull(buyerCredit) && {
          buyerCredit: {
            amount: buyerCredit
          }
        }),
      ...(state.transactionInformationHazardInsuranceAmountVisible &&
        !isNull(hazardInsurance) && {
          hazardInsurance: {
            amount: hazardInsurance
          }
        }),
      ...(state.transactionInformationWarrantyPremiumAmountVisible &&
        !isNull(warrantyPremium) && {
          warrantyPremium: {
            amount: warrantyPremium
          }
        }),
      ...(state.transactionInformationSurveyAmendmentSplitVisible && {
        surveyAmendment: {
          split: state.transactionInformationSurveyAmendmentSplit ? 100 : 0
        }
      }),
      ...(state.transactionInformationNaturalHazardDisclosureVisible &&
        !isNull(naturalHazardDisclosure) && {
          naturalHazardDisclosure: {
            amount: naturalHazardDisclosure
          }
        }),
      ...(state.transactionInformationNotaryFeeVisible &&
        !isNull(notaryFee) && {
          notaryFee: {
            amount: notaryFee
          }
        }),
      brokerCommission: {
        ...(state.salesCommissionBrokerCommissionListingVisible && {
          listing: {
            ...(isNull(brokerCommissionListingAmount) &&
              !isNull(brokerCommissionListingRate) && {
                rate: brokerCommissionListingRate
              }),
            ...(!isNull(brokerCommissionListingAmount) && {
              amount: brokerCommissionListingAmount
            })
          }
        }),
        ...(state.salesCommissionBrokerCommissionSellingVisible && {
          selling: {
            ...(isNull(brokerCommissionSellingAmount) &&
              !isNull(brokerCommissionSellingRate) && {
                rate: brokerCommissionSellingRate
              }),
            ...(!isNull(brokerCommissionSellingAmount) && {
              amount: brokerCommissionSellingAmount
            })
          }
        }),
        tax: {
          ...(state.salesCommissionBrokerCommissionTaxSellingVisible &&
            !isNull(state.salesCommissionBrokerCommissionTaxSelling) && {
              selling: state.salesCommissionBrokerCommissionTaxSelling
            }),
          ...(state.salesCommissionBrokerCommissionTaxListingVisible &&
            !isNull(state.salesCommissionBrokerCommissionTaxListing) && {
              listing: state.salesCommissionBrokerCommissionTaxListing
            }),
          ...(state.salesCommissionBrokerCommissionTaxTotalVisible &&
            !isNull(state.salesCommissionBrokerCommissionTaxTotal) && {
              total: state.salesCommissionBrokerCommissionTaxTotal
            })
        },
        ...((!isNull(brokerCommissionTotalAmount) || !isNull(brokerCommissionTotalRate)) &&
          !isNull(brokerCommissionTierRate) && {
            tier: {
              rate: brokerCommissionTierRate
            }
          }),
        total: {
          ...(!isNull(brokerCommissionTotalAmount) && {
            amount: brokerCommissionTotalAmount
          }),
          ...(!isNull(brokerCommissionTotalRate) && {
            rate: brokerCommissionTotalRate
          })
        }
      },
      closingDate: DateTime.fromFormat(state.transactionInformationClosingDate ?? '', 'MM/dd/yyyy').toJSDate(),
      escrow: {
        county:
          state.transactionInformationEscrowCountyVisible && !isNull(state.transactionInformationEscrowCounty)
            ? state.transactionInformationEscrowCounty
            : '',
        state: !isNull(state.transactionInformationEscrowState)
          ? state.transactionInformationEscrowState
          : NetSheetStateEnum.CA,
        ...(state.transactionInformationEscrowCityVisible &&
          !isNull(state.transactionInformationEscrowCity) && {
            city: state.transactionInformationEscrowCity
          }),
        ...(state.transactionInformationSubEscrowVisible && {
          subEscrow: state.transactionInformationSubEscrow
        }),
        ...(state.transactionInformationEscrowFeeSplitVisible && {
          ...(state.transactionInformationEscrowFeeSplit === NetSheetSplitOptionsEnum.Seller && {
            buyerRate: 0,
            sellerRate: 100
          }),
          ...(state.transactionInformationEscrowFeeSplit === NetSheetSplitOptionsEnum.Buyer && {
            buyerRate: 100,
            sellerRate: 0
          }),
          ...(state.transactionInformationEscrowFeeSplit === NetSheetSplitOptionsEnum.Split && {
            buyerRate: 50,
            sellerRate: 50
          })
        })
      },
      hoa: {
        ...(state.taxesHomeownersDuesAmountVisible &&
          !isNull(hoaAmount) && {
            amount: hoaAmount
          }),
        ...(state.taxesHomeownersDuesDueDateVisible &&
          !isNull(state.taxesHomeownersDuesDueDate) && {
            dueDate: DateTime.fromFormat(state.taxesHomeownersDuesDueDate, 'MM/dd/yyyy').toJSDate()
          }),
        ...(state.taxesHomeownersDuesPaidVisible && {
          paid: state.taxesHomeownersDuesPaid
        }),
        ...(state.taxesHomeownersDuesTermVisible &&
          !isNull(state.taxesHomeownersDuesTerm) && {
            term: state.taxesHomeownersDuesTerm
          })
      },
      loans: {
        ...(state.transactionInformationNewLoanVisible && {
          newLoan: state.transactionInformationNewLoan
        }),
        primary: {
          ...(state.transactionInformationLoanPrimaryAmountVisible &&
            !isNull(loanPrimaryBalance) && {
              amount: loanPrimaryBalance
            }),
          ...(state.transactionInformationLoanPrimaryInterestRateVisible &&
            !isNull(loanPrimaryInterestRate) && {
              interestRate: loanPrimaryInterestRate
            }),
          ...(state.transactionInformationLoanPrimaryOriginationFeesVisible &&
            !isNull(loanPrimaryOriginationFees) && {
              ...(inRange(loanPrimaryOriginationFees, 0, 21)
                ? {
                    originationFeesPercent: loanPrimaryOriginationFees
                  }
                : {
                    originationFees: loanPrimaryOriginationFees
                  })
            })
        }
      },
      miscItems: compact(
        map(state.miscItems, (item) => {
          const amount = cleanUpNullishMaskValue(item.amount, amountPrefix);
          return !isNull(amount) && !isNull(item.description) && !isNull(item.type)
            ? {
                amount,
                description: item.description,
                type: item.type
              }
            : null;
        })
      ),
      mortgages: {
        ...(state.transactionInformationMortgagePayoffCountVisible &&
          !isNull(state.transactionInformationMortgagePayoffCount) && {
            payoffCount: state.transactionInformationMortgagePayoffCount
          }),

        ...(state.mortgagePrimaryBalanceVisible &&
          !isNull(mortgagePrimaryBalance) && {
            primary: {
              balance: mortgagePrimaryBalance,
              ...(!isNull(mortgagePrimaryInterestRate) && {
                interestRate: mortgagePrimaryInterestRate
              }),
              ...(!isNull(mortgagePrimaryPrepayPenalty) && {
                prepayPenalty: mortgagePrimaryPrepayPenalty
              }),
              ...(state.mortgagePrimaryLienCloseDateVisible &&
                !isNull(state.mortgagePrimaryLienCloseDate) && {
                  lienCloseDate: DateTime.fromFormat(state.mortgagePrimaryLienCloseDate, 'MM/dd/yyyy').toJSDate()
                })
            }
          }),
        ...(state.mortgageSecondaryBalanceVisible &&
          !isNull(mortgageSecondaryBalance) && {
            secondary: {
              balance: mortgageSecondaryBalance,
              ...(!isNull(mortgageSecondaryInterestRate) && {
                interestRate: mortgageSecondaryInterestRate
              }),
              ...(!isNull(mortgageSecondaryPrepayPenalty) && {
                prepayPenalty: mortgageSecondaryPrepayPenalty
              })
            }
          })
      },
      options: {
        ...(state.transactionInformationFirptaVisible && {
          firpta: !state.transactionInformationFirpta
        }),
        ...(state.transactionInformationFtbVisible && {
          ftb: !state.transactionInformationFtb
        }),
        ...(state.transactionInformationHarptaVisible && {
          harpta: state.transactionInformationHarpta
        }),
        ...(state.transactionInformationReoVisible && {
          reo: state.transactionInformationReo
        }),
        ...(state.transactionInformationShortSaleVisible && {
          shortSale: state.transactionInformationShortSale
        }),
        ...(state.transactionInformationPersonalResidenceVisible && {
          personalResidence: state.transactionInformationPersonalResidence
        })
      },
      ownersTitleInsurance: {
        ...(state.transactionInformationOwnersPolicySplitVisible && {
          ...(state.transactionInformationOwnersPolicySplit == NetSheetSplitOptionsEnum.Seller && {
            buyerRate: 0,
            sellerRate: 100
          }),
          ...(state.transactionInformationOwnersPolicySplit == NetSheetSplitOptionsEnum.Buyer && {
            buyerRate: 100,
            sellerRate: 0
          }),
          ...(state.transactionInformationOwnersPolicySplit == NetSheetSplitOptionsEnum.Split && {
            buyerRate: 50,
            sellerRate: 50
          })
        }),
        ...(state.transactionInformationOwnersExemptionVisible && {
          exemption: state.transactionInformationOwnersExemption
        })
      },
      party: {
        ...(state.customerInformationPartyFullNameVisible && !isNull(state.customerInformationPartyFullName)
          ? {
              fullName: state.customerInformationPartyFullName
            }
          : { fullName: '' }),
        ...(state.customerInformationPartyFirstNameVisible &&
          !isNull(state.customerInformationPartyFirstName) && {
            firstName: state.customerInformationPartyFirstName
          }),
        ...(state.customerInformationPartyLastNameVisible &&
          !isNull(state.customerInformationPartyLastName) && {
            lastName: state.customerInformationPartyLastName
          }),
        type: state.customerInformationPartyType
      },
      property: {
        ...(!isNull(state.transactionInformationPropertyState)
          ? {
              state: state.transactionInformationPropertyState
            }
          : {
              state: NetSheetStateEnum.CA
            }),
        ...(!isNull(state.transactionInformationPropertyCounty) && {
          county: state.transactionInformationPropertyCounty
        }),
        ...(state.transactionInformationPropertyCityVisible &&
          !isNull(state.transactionInformationPropertyCity) && {
            city: state.transactionInformationPropertyCity
          }),
        ...(state.transactionInformationPropertyZipVisible &&
          !isNull(state.transactionInformationPropertyZip) && {
            zip: state.transactionInformationPropertyZip
          }),
        ...(!isNull(state.transactionInformationPropertyAddress) && {
          address: state.transactionInformationPropertyAddress
        }),
        ...(state.transactionInformationPropertyIncorporatedAreaVisible && {
          incorporatedArea: state.transactionInformationPropertyIncorporatedArea
        })
      },
      salesPrice: !isNull(salesPrice) ? salesPrice : 0,
      taxes: {
        ...(state.taxesNextInstallmentVisible &&
          !isNull(taxesNextInstallment) && {
            nextInstallment: taxesNextInstallment
          }),
        ...(state.taxesPaidVisible && {
          paid: state.taxesPaid
        }),
        ...((state.taxesPaidToDatePickerVisible || state.taxesPaidToDateDropdownVisible) &&
          !isNull(taxesPaidToDate) && {
            paidToDate: taxesPaidToDate
          }),
        ...(state.taxesTermVisible &&
          !isNull(state.taxesTerm) && {
            term: state.taxesTerm
          }),
        ...(state.taxesVisible &&
          !isNull(taxes) && {
            amount: taxes
          })
      },
      user: {
        ...(state.userInformationFirstName && {
          firstName: state.userInformationFirstName
        }),
        ...(state.userInformationLastName && {
          lastName: state.userInformationLastName
        }),
        ...(state.userInformationCompany && {
          company: state.userInformationCompany
        }),
        ...(state.userInformationPhone && {
          phone: state.userInformationPhone
        }),
        ...(state.userInformationEmail && {
          email: state.userInformationEmail
        })
      }
    };
  }
);

export const selectNetSheetCalculatorResult = createSelector(selectNetSheetFeature, (state) => state.calculatorResult);
export const selectHasNetSheetCalculatorResult = createSelector(
  selectNetSheetCalculatorResult,
  (report) => !isNull(report)
);
export const selectNetSheetCalculatorResultClient = createSelector(
  selectNetSheetCalculatorResult,
  (calculatorResult) => calculatorResult?.client
);
export const selectNetSheetCalculatorResultHeader = createSelector(
  selectNetSheetCalculatorResult,
  (calculatorResult) => calculatorResult?.header
);

export const selectNetSheetCalculatorResultReport = createSelector(
  selectNetSheetCalculatorResult,
  (calculatorResult) => calculatorResult?.body.report
);
export const selectNetSheetCalculatorReportSections = createSelector(
  selectNetSheetCalculatorResultReport,
  (report) => report?.sections ?? []
);
export const selectNetSheetCalculatorReportSubtitle = createSelector(
  selectNetSheetCalculatorResultReport,
  (report) => report?.subtitle ?? []
);
export const selectNetSheetCalculatorReportTitle = createSelector(
  selectNetSheetCalculatorResultReport,
  (report) => report?.title ?? ''
);

export const selectNetSheetCalculatorResultFooter = createSelector(
  selectNetSheetCalculatorResult,
  (calculatorResult) => calculatorResult?.footer ?? null
);
export const selectHasNetSheetCalculatorReportFooter = createSelector(
  selectNetSheetCalculatorResultFooter,
  (footerData) => !isNull(footerData)
);
export const selectHasNetSheetCalculatorReportFooterNotes = createSelector(
  selectNetSheetCalculatorResultFooter,
  (footerData) => footerData?.note ?? []
);
export const selectHasNetSheetCalculatorReportFooterDisclaimers = createSelector(
  selectNetSheetCalculatorResultFooter,
  (footerData) => footerData?.disclaimer ?? []
);
export const selectHasNetSheetCalculatorReportFooterCopyright = createSelector(
  selectNetSheetCalculatorResultFooter,
  (footerData) => footerData?.copyright ?? ''
);

export const selectNetSheetExportReportPdfBody = createSelector(
  selectNetSheetCalculatorReportSections,
  selectNetSheetCalculatorReportSubtitle,
  selectNetSheetCalculatorReportTitle,
  selectHasNetSheetCalculatorReportFooterNotes,
  selectHasNetSheetCalculatorReportFooterDisclaimers,
  selectHasNetSheetCalculatorReportFooterCopyright,
  selectNetSheetCalculatorResultClient,
  selectNetSheetCalculatorResultHeader,
  (sections, subtitle, title, notes, disclaimers, copyright, client, header) => ({
    body: {
      report: {
        sections: map(sections, (section) => ({
          items: map(section.items, (item) => ({
            accent: item.accent,
            description: item.description,
            emphasise: item.emphasise,
            highlight: item.highlight,
            isCredit: item.isCredit,
            name: item.name,
            semiEmphasize: !!item['semiEmphasize'],
            value: item.value
          })),
          subtitle: section.subtitle,
          title: section.title
        })),
        subtitle,
        title: title
      }
    },
    client: {
      name: client?.name ?? ''
    },
    footer: {
      copyright: copyright,
      disclaimer: disclaimers,
      note: notes
    },
    header: {
      agent: {
        company: header?.agent.company ?? '',
        email: header?.agent.email ?? '',
        name: header?.agent.name ?? '',
        phone: header?.agent.phone ?? ''
      }
    }
  })
);
